import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PhotoIcon, PlusIcon, TrashIcon, ArrowUpTrayIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { createBucketIfNotExists, uploadFile, uploadMultipleFiles, resizeImage } from '../lib/storage';

interface Photo {
  url: string;
  caption: string;
}

interface PhotoCategory {
  name: string;
  photos: Photo[];
}

interface Tournament {
  id: string;
  name: string;
}

interface PhotoGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (categories: PhotoCategory[], tournamentId: string | null, title: string) => void;
  initialCategories?: PhotoCategory[];
  initialTournamentId?: string;
  initialTitle?: string;
  onTournamentChange?: (id: string) => void;
  galleryFolder?: string;
}

const slugifyTitle = (title: string): string =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function PhotoGalleryModal({
  isOpen,
  onClose,
  onSave,
  initialCategories = [],
  initialTournamentId = '',
  initialTitle = '',
  onTournamentChange,
  galleryFolder = 'gallery',
}: PhotoGalleryModalProps) {
  const [categories, setCategories] = useState<PhotoCategory[]>([]);
  const [galleryTitle, setGalleryTitle] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [uploadErrors, setUploadErrors] = useState<{[key: string]: string}>({});
  const [bulkTotal, setBulkTotal] = useState<number>(0);
  const [bulkCompleted, setBulkCompleted] = useState<number>(0);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setCategories(initialCategories.length > 0 ? [...initialCategories] : [{ name: '', photos: [] }]);
      setSelectedTournamentId(initialTournamentId || '');
      setGalleryTitle(initialTitle || '');
      setUploadProgress({});
      setUploadErrors({});
    }
  }, [isOpen, initialCategories, initialTournamentId, initialTitle]);

  const fetchTournaments = async () => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('id, name')
        .order('start_date', { ascending: false });
      setTournaments(data || []);
    } catch {}
  };

  const handleTournamentChange = (id: string) => {
    setSelectedTournamentId(id);
    onTournamentChange?.(id);
  };

  const addCategory = () => {
    setCategories([...categories, { name: '', photos: [] }]);
  };

  const updateCategory = (index: number, name: string) => {
    const updatedCategories = [...categories];
    if (updatedCategories[index]) {
      updatedCategories[index] = { 
        ...updatedCategories[index], 
        name,
        photos: updatedCategories[index].photos || []
      };
      setCategories(updatedCategories);
    }
  };

  const removeCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const addPhoto = (categoryIndex: number) => {
    const updatedCategories = [...categories];
    const category = updatedCategories[categoryIndex];
    if (category) {
      category.photos = [...(category.photos || []), { url: '', caption: '' }];
      setCategories(updatedCategories);
    }
  };

  const updatePhoto = (categoryIndex: number, photoIndex: number, field: keyof Photo, value: string) => {
    const updatedCategories = [...categories];
    const category = updatedCategories[categoryIndex];
    if (category && category.photos && category.photos[photoIndex]) {
      category.photos[photoIndex] = {
        ...category.photos[photoIndex],
        [field]: value
      };
      setCategories(updatedCategories);
    }
  };

  const removePhoto = (categoryIndex: number, photoIndex: number) => {
    const updatedCategories = [...categories];
    const category = updatedCategories[categoryIndex];
    if (category && category.photos) {
      category.photos = category.photos.filter((_, i) => i !== photoIndex);
      setCategories(updatedCategories);
    }
  };

  const handleFileUpload = async (categoryIndex: number, photoIndex: number, file: File) => {
    if (!file) return;
    
    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, WebP, or GIF)');
      setUploadErrors(prev => ({...prev, [`${categoryIndex}-${photoIndex}`]: 'Invalid file type'}));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      setUploadErrors(prev => ({...prev, [`${categoryIndex}-${photoIndex}`]: 'File too large'}));
      return;
    }
    
    try {
      setUploading(true);
      const uploadKey = `${categoryIndex}-${photoIndex}`;
      setUploadProgress(prev => ({...prev, [uploadKey]: 0}));
      setUploadErrors(prev => ({...prev, [uploadKey]: ''}));
      
      // Ensure the images bucket exists
      await createBucketIfNotExists('images', true);
      
      const activeFolder = galleryTitle.trim() ? slugifyTitle(galleryTitle.trim()) : (galleryFolder || 'gallery');
      const url = await uploadFile(file, 'images', `gallery/${activeFolder}`, (progress) => {
        setUploadProgress({...uploadProgress, [uploadKey]: progress});
      });
      
      // Update the photo URL
      const updatedCategories = [...categories];
      if (updatedCategories[categoryIndex] && 
          updatedCategories[categoryIndex].photos && 
          updatedCategories[categoryIndex].photos[photoIndex]) {
        updatedCategories[categoryIndex].photos[photoIndex].url = url;
        setCategories(updatedCategories);
      }
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setUploadErrors(prev => ({...prev, [`${categoryIndex}-${photoIndex}`]: 'Upload failed'}));
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async (categoryIndex: number, files: FileList) => {
    if (!files.length) return;

    try {
      setUploading(true);
      const updatedCategories = [...categories];
      const category = updatedCategories[categoryIndex] || { name: '', photos: [] };

      if (!category) return;

      await createBucketIfNotExists('images', true);

      const fileArray = Array.from(files);

      const validFiles = fileArray.filter(file => {
        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        const validSize = file.size <= 5 * 1024 * 1024;

        if (!validTypes.includes(file.type)) {
          toast.error(`${file.name}: Invalid file type`);
          return false;
        }

        if (!validSize) {
          toast.error(`${file.name}: File size must be less than 5MB`);
          return false;
        }

        return true;
      });

      if (validFiles.length === 0) {
        toast.error('No valid files to upload');
        setUploading(false);
        return;
      }

      setBulkTotal(validFiles.length);
      setBulkCompleted(0);

      const completedSet = new Set<number>();

      const activeFolder = galleryTitle.trim() ? slugifyTitle(galleryTitle.trim()) : (galleryFolder || 'gallery');
      const urls = await uploadMultipleFiles(
        validFiles,
        'images',
        `gallery/${activeFolder}`,
        (progress, index) => {
          const uploadKey = `bulk-${categoryIndex}-${index}`;
          setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
          if (progress === 100 && !completedSet.has(index)) {
            completedSet.add(index);
            setBulkCompleted(completedSet.size);
          }
        }
      );

      urls.forEach((url) => {
        category.photos.push({ url, caption: '' });
      });

      setCategories(updatedCategories);
      toast.success(`${urls.length} images uploaded successfully`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload some images');
    } finally {
      setUploading(false);
      setBulkTotal(0);
      setBulkCompleted(0);
      setUploadProgress({});
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!galleryTitle.trim()) {
      return;
    }
    const validCategories = categories.filter(
      category => category.name.trim() !== '' && category.photos && category.photos.length > 0
    );
    onSave(validCategories, selectedTournamentId || null, galleryTitle.trim());
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {uploading && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-blue-800">
                        {bulkTotal > 0
                          ? `Uploading ${bulkCompleted} of ${bulkTotal} images...`
                          : 'Uploading image...'}
                      </span>
                      {bulkTotal > 0 && (
                        <span className="text-xs text-blue-600 font-semibold">
                          {Math.round((bulkCompleted / bulkTotal) * 100)}%
                        </span>
                      )}
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                        style={{
                          width: bulkTotal > 0
                            ? `${Math.round((bulkCompleted / bulkTotal) * 100)}%`
                            : `${Object.values(uploadProgress)[0] ?? 0}%`
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Photo Gallery Management
                  </Dialog.Title>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <TrophyIcon className="w-4 h-4 text-golf-orange" />
                    <select
                      value={selectedTournamentId}
                      onChange={e => handleTournamentChange(e.target.value)}
                      className="text-sm rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green py-1.5"
                    >
                      <option value="">No tournament</option>
                      {tournaments.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gallery Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={galleryTitle}
                    onChange={e => setGalleryTitle(e.target.value)}
                    placeholder="e.g. 2025 Welcoming Event"
                    required
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                  />
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    {categories.map((category, categoryIndex) => (
                      <div key={categoryIndex} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => updateCategory(categoryIndex, e.target.value)}
                            placeholder="Album Name"
                            className="block w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          />
                          <button
                            type="button"
                            onClick={() => removeCategory(categoryIndex)}
                            className="ml-2 text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Bulk upload section */}
                        <div className="mb-4 p-4 border border-dashed border-gray-300 rounded-lg">
                          {uploading && bulkTotal > 0 ? (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-700 text-center mb-3">
                                Uploading {bulkCompleted} of {bulkTotal} images
                              </p>
                              {Array.from({ length: bulkTotal }).map((_, i) => {
                                const key = `bulk-${categoryIndex}-${i}`;
                                const pct = uploadProgress[key] ?? 0;
                                return (
                                  <div key={i} className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                      <div
                                        className="h-1.5 rounded-full bg-golf-orange transition-all duration-200"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center">
                              <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <p className="mt-1 text-sm text-gray-600">
                                Drag and drop images here, or click to select files
                              </p>
                              <p className="text-xs text-gray-500">
                                Supports: JPG, PNG, WebP, GIF (up to 5MB each)
                              </p>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                className="hidden"
                                id={`bulk-upload-${categoryIndex}`}
                                onChange={(e) => e.target.files && handleBulkUpload(categoryIndex, e.target.files)}
                                disabled={uploading}
                              />
                              <button
                                type="button"
                                onClick={() => document.getElementById(`bulk-upload-${categoryIndex}`)?.click()}
                                className="mt-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golf-orange"
                                disabled={uploading}
                              >
                                Select Files
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {category.photos && category.photos.map((photo, photoIndex) => (
                            <div key={photoIndex} className="space-y-2">
                              <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                                {photo.url ? (
                                  <img
                                    src={photo.url}
                                    alt={photo.caption || 'Gallery image'}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <PhotoIcon className="w-12 h-12 text-gray-400" />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removePhoto(categoryIndex, photoIndex)}
                                  className="absolute top-2 right-2 p-1 bg-white/80 rounded-full text-red-600 hover:text-red-800"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                                {uploadProgress[`${categoryIndex}-${photoIndex}`] > 0 &&
                                 uploadProgress[`${categoryIndex}-${photoIndex}`] < 100 && (
                                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center px-4">
                                    <span className="text-white text-sm font-semibold mb-2">
                                      {uploadProgress[`${categoryIndex}-${photoIndex}`]}%
                                    </span>
                                    <div className="w-full bg-white/30 rounded-full h-2 overflow-hidden">
                                      <div
                                        className="h-2 rounded-full bg-golf-orange transition-all duration-200"
                                        style={{ width: `${uploadProgress[`${categoryIndex}-${photoIndex}`]}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                                {uploadErrors[`${categoryIndex}-${photoIndex}`] && (
                                  <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                                    <div className="text-white text-center bg-red-600/80 p-2 rounded">
                                      <p className="text-sm">{uploadErrors[`${categoryIndex}-${photoIndex}`]}</p>
                                      <button 
                                        className="mt-2 px-2 py-1 bg-white text-red-600 rounded text-xs"
                                        onClick={() => setUploadErrors({...uploadErrors, [`${categoryIndex}-${photoIndex}`]: ''})}
                                      >
                                        Dismiss
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <input
                                type="url"
                                value={photo.url || ''}
                                onChange={(e) => updatePhoto(categoryIndex, photoIndex, 'url', e.target.value)}
                                placeholder="Photo URL"
                                className="block w-full text-sm rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              />
                              <input
                                type="text"
                                value={photo.caption || ''}
                                onChange={(e) => updatePhoto(categoryIndex, photoIndex, 'caption', e.target.value)}
                                placeholder="Caption"
                                className="block w-full text-sm rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              />
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                id={`file-upload-${categoryIndex}-${photoIndex}`}
                                onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload(categoryIndex, photoIndex, e.target.files[0])}
                                disabled={uploading}
                              />
                              <button
                                type="button"
                                onClick={() => document.getElementById(`file-upload-${categoryIndex}-${photoIndex}`)?.click()}
                                className="w-full px-3 py-1 text-xs border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golf-orange"
                                disabled={uploading}
                              >
                                {photo.url && photo.url.trim() !== '' ? 'Replace Image' : 'Upload Image'}
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addPhoto(categoryIndex)}
                            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 hover:text-golf-orange hover:border-golf-orange transition-colors"
                          >
                            <PlusIcon className="w-8 h-8" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addCategory}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-golf-orange hover:border-golf-orange transition-colors"
                  >
                    Add New Album
                  </button>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Save Gallery'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}